import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

export const LegalLayout = ({ title, metaDescription, lastUpdated, children }) => {
  useEffect(() => {
    document.title = `${title} | FinScope`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', metaDescription);
    return () => {
      document.title = 'FinScope | Control Financiero';
      if (previous) meta.setAttribute('content', previous);
    };
  }, [title, metaDescription]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-logo-link">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-foreground block leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                FinScope
              </span>
              <span className="text-xs text-muted-foreground">Inteligencia Financiera</span>
            </div>
          </Link>
          <Button asChild variant="outline" size="sm" data-testid="legal-back-button">
            <Link to="/login">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a FinScope
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <article className="legal-content">
          <h1
            className="text-3xl sm:text-4xl font-bold text-foreground mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          <p className="text-sm text-muted-foreground mb-10" data-testid="legal-last-updated">
            Última actualización: {lastUpdated}
          </p>
          <div className="space-y-8 text-sm sm:text-base leading-relaxed text-foreground/90">
            {children}
          </div>
        </article>
      </main>

      <footer className="border-t border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} FinScope. Plataforma de inteligencia financiera.
          </p>
          <nav className="flex items-center gap-5 text-xs sm:text-sm" data-testid="legal-footer-nav">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-privacy-link">
              Política de Privacidad
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-terms-link">
              Términos y Condiciones
            </Link>
            <Link to="/data-deletion" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-data-deletion-link">
              Eliminación de Datos
            </Link>
            <Link to="/" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-home-link">
              Inicio
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export const LegalSection = ({ number, title, children }) => (
  <section>
    <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {number}. {title}
    </h2>
    <div className="space-y-3 text-muted-foreground">{children}</div>
  </section>
);
